import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight, EnvelopeSimple, InstagramLogo } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Seo } from "../../kit.jsx";
import { AAROH_ALBUM } from "../../data/album.js";
import { AlbumImg } from "./media.jsx";
import { Btn, Rise } from "./pgkit.jsx";
import "./programmes.css";

const BOARD_CHECKED = "30 September 2026";

const MAIL = `mailto:founder@projectgrid.org?subject=${encodeURIComponent("Tell me about the next GRID event")}&body=${encodeURIComponent(
  "Hi Project GRID,\n\nPlease email me when a new event opens.\n\nMy name:\nI am a (student / parent / teacher / school):\nMy grade or age group:\nCity:\n",
)}`;

/* Only confirmed events go here. Status: "open" (applications open), "announced" (dates confirmed, not open yet), "closed". */
const EVENTS = [
  {
    status: "closed",
    name: "AAROH 2026",
    runBy: "Project GRID, with Tachyon India and CiPD at IIIT Delhi",
    when: "1–2 August 2026",
    where: "IIIT Delhi",
    note: "Finished. 363 young innovators took part.",
    to: "/programmes/aaroh",
    cta: "Read the recap",
  },
  {
    status: "closed",
    name: "Aurora Global Hackathon 2026",
    runBy: "Project GRID",
    when: "From January 2026",
    where: "Online",
    note: "Finished. The Top 20 entered incubation.",
    to: "/programmes/aurora",
    cta: "See the results",
  },
  {
    status: "closed",
    name: "Metamorphosis",
    runBy: "Tale of Humankind Foundation and IIT Delhi. GRID was the outreach partner.",
    when: "May 2026",
    where: "IIT Delhi",
    note: "Finished. 150 students from 18 states took part.",
    to: "/programmes/metamorphosis",
    cta: "How we supported it",
  },
];

const STATUS = {
  open: ["Open", "Applications are open now."],
  announced: ["Announced", "Dates are confirmed. Applications are not open yet."],
  closed: ["Closed", "The event has finished. Read what happened."],
};

const FILTERS = ["all", "open", "announced", "closed"];

function Flap({ text, delay = 0 }) {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return <span>{text}</span>;
  return (
    <span className="pg-flap" aria-label={text}>
      {text.split("").map((char, index) => (
        <span className="pg-flap__cell" aria-hidden="true" key={`${char}-${index}`}>
          <motion.span
            initial={{ y: "-105%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: delay + index * 0.035, ease: EASE_OUT }}
          >
            {char}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

const LAST_ROOM = AAROH_ALBUM.find((item) => item.id === "M682");

function NotifyPhoto() {
  const reduceMotion = useReducedMotion();
  return (
    <figure className="pg-notify__photo">
      <motion.span
        initial={reduceMotion ? false : { clipPath: "inset(16% 0% 0% 0% round 28px)" }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.1, ease: EASE_OUT }}
      >
        <AlbumImg photo={LAST_ROOM} sizes="(max-width: 899px) 100vw, 40vw" />
      </motion.span>
      <figcaption>Our most recent event: the view from the lectern at AAROH, 1–2 August 2026.</figcaption>
    </figure>
  );
}

function Board() {
  const [filter, setFilter] = useState("all");
  const reduceMotion = useReducedMotion();
  const counts = Object.fromEntries(FILTERS.map((key) => [key, key === "all" ? EVENTS.length : EVENTS.filter((event) => event.status === key).length]));
  const shown = filter === "all" ? EVENTS : EVENTS.filter((event) => event.status === filter);
  return (
    <section className="pg-board" id="board" aria-labelledby="pg-board-title">
      <div className="pg-board__top">
        <h2 id="pg-board-title">The board</h2>
        <div className="pg-board__filters" role="group" aria-label="Filter by status">
          {FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              className={`pg-filter ${filter === key ? "is-on" : ""}`}
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
            >
              {key === "all" ? "All" : STATUS[key][0]}
              <span>{counts[key]}</span>
            </button>
          ))}
        </div>
      </div>
      <div className={`pg-board__cols ${shown.length ? "" : "is-hidden"}`} aria-hidden="true">
        <span>Status</span><span>Event</span><span>When</span><span>Where</span><span />
      </div>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={filter}
          initial={reduceMotion ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? undefined : { opacity: 0, y: -6, transition: { duration: 0.15 } }}
          transition={{ duration: 0.4, ease: EASE_OUT }}
        >
          {shown.length ? (
            <ul className="pg-board__rows">
              {shown.map((event, index) => (
                <li key={event.name} className="pg-row">
                  <span className={`pg-chip pg-chip--${event.status}`}>
                    <Flap text={STATUS[event.status][0]} delay={0.1 + index * 0.12} />
                  </span>
                  <div className="pg-row__event">
                    <strong>{event.name}</strong>
                    <span>{event.runBy}</span>
                    <small>{event.note}</small>
                  </div>
                  <span className="pg-row__meta" data-label="When">{event.when}</span>
                  <span className="pg-row__meta" data-label="Where">{event.where}</span>
                  <Link className="pg-row__link" to={event.to}>
                    {event.cta}<span className="sr-only">: {event.name}</span> <ArrowRight weight="bold" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="pg-board__empty">
              <span className={`pg-chip pg-chip--${filter}`}>{STATUS[filter][0]}</span>
              <h3>{filter === "open" ? "Nothing is open right now." : "Nothing is announced yet."}</h3>
              <p>When GRID confirms a new event, it appears here with dates, who can apply and any fee.</p>
              <Btn href={MAIL} icon={<EnvelopeSimple weight="bold" aria-hidden="true" className="pg-btn__after" />}>Email me when one opens</Btn>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      <p className="pg-board__checked">Board last checked {BOARD_CHECKED}.</p>
    </section>
  );
}

export default function UpcomingPage() {
  const openCount = EVENTS.filter((event) => event.status === "open").length;
  const announcedCount = EVENTS.filter((event) => event.status === "announced").length;
  const headline = openCount
    ? `${openCount} ${openCount === 1 ? "event is" : "events are"} open now.`
    : "Nothing is open right now.";
  return (
    <PageFrame className="rd-page pg-page pg-upcoming-page">
      <Seo
        title="Upcoming Events | Project GRID"
        path="/programmes/upcoming"
        description="Follow confirmed Project GRID programme and event announcements without speculative dates."
      />
      <main className="pg-upcoming">
        <section className="pg-uh" aria-labelledby="pg-uh-title">
          <Rise as="p" className="gh-mono pg-kicker" onMount>Upcoming events</Rise>
          <MaskWords as="h1" id="pg-uh-title" text={headline} onMount delay={0.05} />
          <div className="pg-uh__row">
            <Rise as="p" onMount delay={0.35}>
              {openCount || announcedCount
                ? "Confirmed events are listed below with dates, who can apply and any fee."
                : "No GRID event is taking applications or has confirmed dates yet. When one does, it goes on this board with dates, who can apply and any fee."}
            </Rise>
            <Rise className="gh-actions" onMount delay={0.45}>
              <Btn href={MAIL} icon={<EnvelopeSimple weight="bold" aria-hidden="true" className="pg-btn__after" />}>Email me when one opens</Btn>
              <Btn href="#board" variant="ghost">See the board</Btn>
            </Rise>
          </div>
        </section>

        <Board />

        <section className="pg-notify" aria-labelledby="pg-notify-title">
          <div className="pg-notify__head">
            <MaskWords id="pg-notify-title" text="Hear about the next one first." />
            <p>Email reaches the team directly. Instagram carries short announcements. The Aurora site carries Aurora news.</p>
            <NotifyPhoto />
          </div>
          <ul className="pg-notify__list">
            <Rise as="li">
              <a href={MAIL}>
                <EnvelopeSimple weight="bold" aria-hidden="true" />
                <span><strong>Email founder@projectgrid.org</strong><small>Ask to be told when the next event opens. Mention your grade or age group and city.</small></span>
                <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </Rise>
            <Rise as="li" delay={0.06}>
              <a href="https://www.instagram.com/projectgrid_org/" target="_blank" rel="noreferrer">
                <InstagramLogo weight="bold" aria-hidden="true" />
                <span><strong>Follow @projectgrid_org</strong><small>Short announcements and registration updates.</small></span>
                <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </Rise>
            <Rise as="li" delay={0.12}>
              <a href="https://aurora.projectgrid.org/" target="_blank" rel="noreferrer">
                <span className="pg-notify__glyph" aria-hidden="true">A</span>
                <span><strong>Watch aurora.projectgrid.org</strong><small>Watch it for the next Aurora announcement.</small></span>
                <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </Rise>
          </ul>
        </section>

        <section className="pg-key" aria-labelledby="pg-key-title">
          <div>
            <h2 id="pg-key-title">What each listing will show</h2>
            <ul className="pg-key__fields">
              {["Dates", "Who can apply", "Fee, or free", "Who runs it", "How to apply"].map((field) => <li key={field}>{field}</li>)}
            </ul>
          </div>
          <div>
            <h2>What the labels mean</h2>
            <dl className="pg-key__status">
              {Object.entries(STATUS).map(([key, [label, meaning]]) => (
                <div key={key}>
                  <dt><span className={`pg-chip pg-chip--${key}`}>{label}</span></dt>
                  <dd>{meaning}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
